import {stringify} from "comment-json";
import {omit, startCase} from "lodash";
import {NodeId, NodeType} from "../../../../model/Graph";
import {Playshot} from "../../../../model/Playshot";
import {makeAsyncIterable} from "../../../async-iterable/make-async-iterable";
import {CodyResponse, CodyResponseType} from "../../../Cody";
import {
  convertJsonSchemaToShorthandIfPossible
} from "../../../json/schema/convert-json-schema-to-shorthand-if-possible";
import {getService, nodeLabel, removeDefaultService} from "../../default-service";
import {ProophBoardDescriptionKeys} from "../../descriptions";
import {
  PlayshotApplyContext,
  PlayshotChanged,
  PlayshotChangedDiff,
  PlayshotChangedOperation,
  PlayshotChangedType
} from "../PlayshotChanged";
import {PlayCommandRuntimeInfo} from "./command-definitions";
import {prepareDiffCommand} from "./prepare-diff-command";
import {getNodeByIdOrFQCN} from "../../../cody-wizard/node/get-node-by-id-or-fqcn";

export class CommandRenamed implements PlayshotChanged {
  public readonly name: string;
  public readonly type: PlayshotChangedType = 'CommandRenamed';
  public readonly operation: PlayshotChangedOperation = 'changed';
  public readonly nodeId: NodeId;
  public readonly nodeType = NodeType.command;
  public readonly diff: PlayshotChangedDiff;

  private changedCommand: PlayCommandRuntimeInfo;
  private oldName: string;
  private newName: string;

  constructor(newName: string, nodeId: string, command: PlayCommandRuntimeInfo, oldCommand: PlayCommandRuntimeInfo, newPlayshot: Playshot, oldPlayshot: Playshot) {
    this.newName = newName;
    this.oldName = oldCommand.desc.name;


    const newRules = newPlayshot.playConfig.commandHandlers[command.desc.name] || [];
    let oldInfo = oldPlayshot.playConfig.commands[this.oldName];

    if(!oldInfo) {
      oldInfo = command;
    }

    const oldRules = oldPlayshot.playConfig.commandHandlers[this.oldName] || [];

    this.name = this.newName;
    this.nodeId = nodeId;
    this.diff = {
      new: prepareDiffCommand(command, newRules),
      old: prepareDiffCommand(oldInfo, oldRules),
    };
    this.changedCommand = command;
  }

  public async apply({syncContext, graph, playshot, addedNodes, elementsTree}: PlayshotApplyContext): Promise<AsyncIterable<CodyResponse>> {
    if(addedNodes[this.nodeId]) {
      return makeAsyncIterable({
        cody: ``,
        type: CodyResponseType.Empty
      })
    }

    const existingNode = getNodeByIdOrFQCN(this.nodeId, this.name, NodeType.command, graph, elementsTree);

    if(!existingNode) {
      return makeAsyncIterable({
        cody: `Hhm, I'm skipping the change "${startCase(this.type)}: ${removeDefaultService(this.name, playshot)}", because the command cannot be found on the board.`,
        details: `There is no element on the board with ID: ${this.nodeId}`,
        type: CodyResponseType.Warning
      })
    }

    const rules = playshot.playConfig.commandHandlers[this.name] || [];
    const service = getService(this.changedCommand.desc, playshot)

    graph.changeNodeMetadata(existingNode, stringify(omit({
      ...this.changedCommand.desc,
      service,
      schema: convertJsonSchemaToShorthandIfPossible(this.changedCommand.schema),
      uiSchema: this.changedCommand.uiSchema,
      rules,
      // @TODO: handle factory
    }, [
      "name",
      ...ProophBoardDescriptionKeys
    ])));

    const similarNodes = elementsTree.getSimilarElements(existingNode);

    graph.changeNodeName(existingNode, nodeLabel(this.newName));

    similarNodes.forEach(sN => {
      graph.changeNodeName(sN, nodeLabel(this.newName));
    })

    graph.focusNode(existingNode);

    return makeAsyncIterable({
      cody: `I've renamed the command "${nodeLabel(this.newName)}" and updated its metadata.`
    });
  }
}
